import moment from 'moment';
import 'moment/locale/pt-br';
import { useEffect, useState, useMemo } from 'react';
import { toast } from 'react-toastify';

import { META_COLLECT_API } from '../../../../api';
import { useSigninContext } from '../../../../context/AuthProviderPages/Signin';
import { IApiResponse, IResumoInicial } from './types';

export const useDashboardData = () => {
  const [loading, setLoading] = useState(false);
  const [kpiData, setKpiData] = useState<IApiResponse | null>(null);
  const [resumoData, setResumoData] = useState<IResumoInicial | null>(null);
  const [loadingFila, setLoadingFila] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [ordemDescarga, setOrdemDescarga] = useState<'DESC' | 'ASC'>('DESC');
  const { path } = useSigninContext();

  useEffect(() => {
    const fetchKpis = async () => {
      setLoading(true);
      try {
        const response = await META_COLLECT_API.get<IApiResponse>(
          '/dashboard/dashboard-inicial',
          {
            headers: { path: path || '' },
          },
        );
        setKpiData(response.data);
      } catch (error) {
        toast.error('Erro ao carregar os dados do dashboard.');
        console.error(error);
      } finally {
        setLoading(false);
      }
    };
    if (path) {
      fetchKpis();
    }
  }, [path]);

  useEffect(() => {
    const fetchResumo = async () => {
      setLoadingFila(true);
      try {
        const responseResumo = await META_COLLECT_API.get<IResumoInicial>(
          `/dashboard/operacional-inicial?ordemDescarga=${ordemDescarga}`,
          {
            headers: { path: path || '' },
          },
        );
        setResumoData(responseResumo.data);
        setCurrentPage(1);
      } catch (error) {
        toast.error('Erro ao carregar a fila de descarga.');
        console.error(error);
      } finally {
        setLoadingFila(false);
      }
    };
    if (path) {
      fetchResumo();
    }
  }, [path, ordemDescarga]);

  const kpis = useMemo(() => {
    if (!kpiData) return null;

    const chartData = (kpiData.volume_por_mes || []).map(item => {
      const dateObj = moment(item.MES_ANO, 'MM/YYYY');
      const key = dateObj.locale('pt-br').format('MMM/YY').replace('.', '');
      const name = key.charAt(0).toUpperCase() + key.slice(1);
      return {
        name,
        volume: item.VOLUME_MES,
      };
    });

    const top3ChartIndexes = [...chartData]
      .map((item, index) => ({ volume: item.volume, index }))
      .sort((a, b) => b.volume - a.volume)
      .slice(0, 3)
      .map(item => item.index);

    return {
      totalLitros: kpiData.total_litros,
      totalViagens: kpiData.total_viagens,
      litragemMedia: kpiData.litragem_media,
      kmTotalRodado: kpiData.km_total_rodado,
      litroPorKm: kpiData.litro_por_km,
      chartData,
      top3ChartIndexes,
    };
  }, [kpiData]);

  const ITEMS_PER_PAGE = 10;
  const totalPages = Math.ceil(
    (resumoData?.ultimas_coletas?.length || 0) / ITEMS_PER_PAGE,
  );
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const paginatedColetas =
    resumoData?.ultimas_coletas?.slice(
      startIndex,
      startIndex + ITEMS_PER_PAGE,
    ) || [];

  return {
    loading,
    resumoData,
    kpis,
    currentPage,
    setCurrentPage,
    totalPages,
    paginatedColetas,
    ordemDescarga,
    setOrdemDescarga,
    loadingFila,
  };
};
